import React, { useState, useEffect } from "react";
import { CheckCircle, Info, Phone, Clock } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import analyticsService from "../lib/AnalyticsService";
import { useInteractionTracking } from "../hooks/useAnalytics";
import insurancePolicyService from "../lib/InsurancePolicyService";
import { useAuth } from "../hooks/useAuth";
import { useJornayaLeadId } from "../useJornayaLeadId";

const REIN_PHONE = "8663552580";
const REIN_PHONE_DISPLAY = "(866) 355-2580";

const isWithinBusinessHours = () => {
  const now = new Date();
  const estTime = new Date(
    now.toLocaleString("en-US", { timeZone: "America/New_York" })
  );
  const hour = estTime.getHours();
  const day = estTime.getDay();
  return day >= 1 && day <= 5 && hour >= 9 && hour < 18;
};

const RewardConfirmationScreen = ({ buyerData, contactData, onContinue }) => {
  const { trackButtonClick } = useInteractionTracking();
  const isSimplyIOA = buyerData?.name?.toLowerCase() === "simplyioa";
  const isInsurify = buyerData?.name?.toLowerCase()?.includes("insurify");
  const isNovo = buyerData?.name?.toLowerCase()?.includes("novo");
  const isAADirect = buyerData?.name?.toLowerCase() === "aadirect";
  const isRein = buyerData?.name?.toLowerCase() === "rein";
  const [shopNowLink, setShopNowLink] = useState(buyerData?.shopNowLink);
  const [loadingRedirect, setLoadingRedirect] = useState(isInsurify || isNovo);
  const [loadingJornaya, setLoadingJornaya] = useState(isNovo);
  const AADIRECT_PHONE = "4048507169"; // ADD THIS LINE
  const AADIRECT_PHONE_DISPLAY = "(404) 850-7169"; // ADD THIS LINE
  const { getIdToken } = useAuth();
  const jornayaLeadId = useJornayaLeadId();

  useEffect(() => {
    analyticsService.trackPageView("you-are-all-set", {
      buyer: buyerData?.name,
    });
    if (isInsurify) {
      const fetchRedirect = async () => {
        try {
          const token = await getIdToken();
          const url = await insurancePolicyService.getInsurifyRedirectUrl(
            token
          );
          setShopNowLink(url);
        } catch (e) {
          console.error("Failed to fetch Insurify redirect URL", e);
        } finally {
          setLoadingRedirect(false);
        }
      };

      fetchRedirect();
    } else if (isNovo) {
      const fetchRedirect = async () => {
        try {
          const token = await getIdToken();
          if (!jornayaLeadId) {
            return;
          }
          setLoadingRedirect(true);
          setLoadingJornaya(false);
          const novoAdResponse = await insurancePolicyService.getNovoAdResponse(
            token,
            jornayaLeadId
          );

          // Fire impression pixel if exists
          if (novoAdResponse?.Ads?.[0]?.ImpressionPixel) {
            const impressionSrc = novoAdResponse.Ads[0].ImpressionPixel.match(
              /src="([^"]+)"/
            )[1].replace("{position}", "1");

            const impressionPixel = new Image();
            impressionPixel.src = impressionSrc;
          }

          // Set shop now link to the click URL
          if (novoAdResponse?.Ads?.[0]?.ClickUrl) {
            setShopNowLink(novoAdResponse.Ads[0].ClickUrl);
          }
        } catch (e) {
          console.error("Failed to fetch Novo ad response", e);
        } finally {
          setLoadingRedirect(false);
        }
      };

      fetchRedirect();
    }
  }, [buyerData?.name, jornayaLeadId, getIdToken, isInsurify, isNovo]);

  const startShopping = () => {
    if (!shopNowLink) return;
    window.open(shopNowLink, "_blank");
  };

  const openDialer = () => {
    trackButtonClick("call-aadirect", {
      step_name: "you-are-all-set",
      buyer: buyerData?.name,
    });
    window.location.href = `tel:${AADIRECT_PHONE}`;
  };

  return (
    <div className="space-y-6">
      {/* Header with celebration */}
      <div className="text-center space-y-4">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
          <CheckCircle className="w-10 h-10 text-green-500" />
        </div>
        <h1 className="text-xl uppercase font-bold">You're All Set</h1>
        {/* Conditional content based on buyer */}
        {isSimplyIOA ? (
          <>
            <p className="text-darkGrey">
              We've saved your contact preferences and passed them to{" "}
              <span style={{ color: "#7A5CFA" }}>SimplyIOA</span>. They have
              your Ownli Insurance Profile, are getting to work, and will be in
              touch within two business days.
            </p>
            <p className="text-darkGrey">
              Your reward will be added to your Ownli Wallet once your profile
              is fully verified.
            </p>
          </>
        ) : isAADirect ? (
          // ADD THIS NEW SECTION:
          <>
            <p className="text-darkGrey">
              Great! Your offer has been accepted and your reward is pending.
            </p>
            <p className="text-darkGrey">
              <strong>Next step:</strong> Call{" "}
              <span style={{ color: "#7A5CFA" }}>{buyerData?.name}</span> at{" "}
              <span style={{ color: "#7A5CFA", fontWeight: "bold" }}>
                {AADIRECT_PHONE_DISPLAY}
              </span>{" "}
              to complete your quote and activate your reward.
            </p>
          </>
        ) : (
          <>
            <p className="text-darkGrey">
              We've saved your contact preferences and passed them to{" "}
              <span style={{ color: "#7A5CFA" }}>
                {buyerData?.name || "our partner"}
              </span>
              . Your reward will be added to your Ownli Wallet once your profile
              is fully verified.
            </p>
            {/* {isNovo && (
              <p className="text-xs text-gray-500">
                Jornaya LeadID: {jornayaLeadId ?? "loading..."}
              </p>
            )} */}
            {/* <p className="text-darkGrey">
              Now, let's set up your Wallet for seamless rewards.
            </p> */}
          </>
        )}
      </div>
      {isNovo && (
        <div
          style={{ marginBottom: "5px" }}
          className="bg-green-50 border-l-4 border-green-400 rounded-lg p-6 mt-4 mb-6 shadow-sm"
        >
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <Info className="w-8 h-8 text-green-600" />
            </div>
            <div>
              <p className="text-[8px] text-green-800 mb-4">
                By clicking “Start Shopping Now”, you agree that Novo and its
                authorized partners may contact you regarding your quotes,
                applications, policy information, or related services at the
                phone number you provide. You authorize us to contact you by
                phone, or SMS including using an automated dialing system or
                prerecorded voice. Your consent is not a condition of purchase.
                Message and data rates may apply. You also agree that if you
                initiate a call to Novo, or if a partner transfers your call or
                submits your information to Novo, Novo may contact you using the
                communication methods described above.
              </p>
            </div>
          </div>
        </div>
      )}
      {/* Wallet Setup Card */}
      {/* <Card className="bg-blue-50 border-blue-100">
        <CardContent className="py-6">
          <div className="flex flex-col items-center">
            <div className="flex items-center gap-2 mb-2">
              <Wallet className="w-6 h-6 text-blue-600" />
              <span className="text-blue-700 font-medium">Reward Pending</span>
            </div>
            <p className="text-sm text-blue-600 text-center mt-2">
              Your reward will be processed as soon as your profile is verified
            </p>
          </div>
        </CardContent>
      </Card> */}

      {/* Contact Method Confirmation */}
      {/* <Card>
        <CardContent className="pt-6">
          <h3 className="font-medium text-gray-900 mb-3">Contact Preferences Saved</h3>
          
          <div className="space-y-2">
            {contactData?.methods?.includes('email') && contactData?.email && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Email:</span>
                <span className="font-medium">{contactData.email}</span>
              </div>
            )}
            
            {contactData?.methods?.includes('phone') && contactData?.phone && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Phone:</span>
                <span className="font-medium">{contactData.phone}</span>
              </div>
            )}
            
            {contactData?.methods?.includes('sms') && contactData?.phone && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">SMS:</span>
                <span className="font-medium">{contactData.phone}</span>
              </div>
            )}
            
            {(!contactData?.methods || contactData.methods.length === 0) && (
              <div className="text-sm text-gray-600">
                Your contact preferences have been saved.
              </div>
            )}
          </div>
        </CardContent>
      </Card>
       */}
      {/* Rein call info box */}
      {isRein && (
        <Card className="border-2 border-[#7B61FF] bg-white rounded-[20px]">
          <CardContent className="p-6">
            <div className="text-center space-y-4">
              {isWithinBusinessHours() ? (
                <>
                  <Phone className="w-12 h-12 text-[#7B61FF] mx-auto" />
                  <div>
                    <h2 className="text-lg font-bold text-gray-900 mb-2">
                      Call {buyerData?.name} to Complete Your Quote
                    </h2>
                    <p className="text-gray-600 text-sm">
                      An expert is ready to help you get your personalized
                      insurance quote
                    </p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">
                      Call this number:
                    </p>
                    <a
                      href={`tel:${REIN_PHONE}`}
                      className="text-2xl font-bold text-[#7B61FF] underline"
                      onClick={() =>
                        trackButtonClick("call-rein", {
                          step_name: "you-are-all-set",
                          phone: REIN_PHONE,
                        })
                      }
                    >
                      {REIN_PHONE_DISPLAY}
                    </a>
                    <p className="text-xs text-gray-500 mt-1">
                      Hours: 9am–6pm EST, Mon–Fri
                    </p>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">
                    Prefer not to call? No worries — you'll receive a call within 1 business day.
                  </p>
                </>
              ) : (
                <>
                  <Clock className="w-12 h-12 text-[#7B61FF] mx-auto" />
                  <div>
                    <h2 className="text-lg font-bold text-gray-900 mb-2">
                      You'll Receive a Call Within 1 Business Day
                    </h2>
                    <p className="text-gray-600 text-sm">
                      {buyerData?.name} experts are available 9am–6pm EST,
                      Mon–Fri. They'll reach out to help you with your
                      personalized insurance quote.
                    </p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">
                      Or call during business hours:
                    </p>
                    <a
                      href={`tel:${REIN_PHONE}`}
                      className="text-2xl font-bold text-[#7B61FF] underline"
                      onClick={() =>
                        trackButtonClick("call-rein", {
                          step_name: "you-are-all-set",
                          phone: REIN_PHONE,
                        })
                      }
                    >
                      {REIN_PHONE_DISPLAY}
                    </a>
                    <p className="text-xs text-gray-500 mt-1">
                      9am–6pm EST, Mon–Fri
                    </p>
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}
      {/* Shop now Button */}
      <button
        disabled={loadingRedirect || loadingJornaya}
        onClick={() => {
          trackButtonClick(isRein ? "continue-to-ownli" : "start-shopping", {
            step_name: "you-are-all-set",
            buyer: buyerData?.name,
          });
          if (isRein) {
            window.location.href = "https://www.ownli.app";
          } else if (isAADirect) {
            openDialer();
          } else {
            startShopping();
          }
        }}
        className={`w-full flex items-center justify-center gap-2 uppercase font-bold px-4 py-3 rounded-[10px] transition-colors ${
          loadingRedirect || loadingJornaya
            ? "bg-gray-300 text-gray-600 cursor-not-allowed"
            : "bg-purpleGradient text-white hover:bg-purpleGradientDark"
        }`}
      >
        {isSimplyIOA
          ? "Learn More"
          : isAADirect
          ? `Call ${AADIRECT_PHONE_DISPLAY}`
          : isRein
          ? "Continue"
          : loadingRedirect
          ? "Preparing your link..."
          : "Start Shopping Now"}

        {/* <ChevronRight className="w-4 h-4" /> */}
      </button>
      {/* Continue Button */}
      {/* <button
        onClick={() => {
          trackButtonClick("go-to-wallet", {
            step_name: "you-are-all-set",
          });
          onContinue();
        }}
        className="w-full flex items-center justify-center gap-2 uppercase font-bold bg-purpleGradient text-white px-4 py-3 rounded-[10px] hover:bg-purpleGradientDark transition-colors"
      >
        Set Up Wallet
      </button> */}
    </div>
  );
};

export default RewardConfirmationScreen;
